/**
 * custom-animations.js — Speake-Marin scroll reveals (site-wide, block-level).
 *
 * Gives a restrained, staggered fade-up to the sections Prestige doesn't already
 * animate — custom and non-custom alike — with zero per-section markup. The
 * reveal is a CSS transition (see custom.css .spm-reveal); this script decides
 * WHEN (native IntersectionObserver) and WHAT (the section's child blocks).
 *
 * Targeting:
 *   - Walk every `main .shopify-section`.
 *   - Skip site chrome (header/footer/announcement) and sections that already
 *     self-animate (they contain a Prestige reveal element / [reveal-on-scroll]).
 *   - Inside each eligible section, find the content wrapper (.section-stack →
 *     .prose → first real child) and stagger-reveal its child BLOCKS. A block
 *     that itself holds self-animating cards is left to the theme.
 *
 * Heroes: the first section is above the fold, so it isn't scroll-revealed here.
 *   - Video hero → CSS load animation (custom.css, no FOUC).
 *   - Slideshow hero → Prestige's own slide animation.
 *
 * No FOUC: only sections BELOW the fold at load are pre-hidden (you can't see a
 * flash on something off-screen).
 *
 * Tuning: distance/opacity/easing/duration in custom.css; stagger + trigger
 * margin below. One edit re-flows the whole site.
 *
 * Accessibility: no motion under prefers-reduced-motion.
 */
(function () {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  var SELF_ANIMATING =
    "image-with-text, image-with-text-overlay, images-with-text-scroll," +
    "slideshow-carousel, [reveal-on-scroll]";
  var CHROME = /shopify-section--(footer|header|announcement)/;

  var TRIGGER_MARGIN = "0px 0px -8% 0px"; // fire a touch before fully in view
  var STAGGER = 0.08; // s between blocks
  var MAX_STAGGER_STEPS = 8; // cap so long sections don't crawl
  var viewportH = window.innerHeight;

  function observeReveal(section, onEnter) {
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            onEnter();
            io.disconnect();
          }
        });
      },
      { rootMargin: TRIGGER_MARGIN }
    );
    io.observe(section);
  }

  function realChildren(el) {
    return [].filter.call(el.children, function (c) {
      return c.tagName !== "STYLE" && c.tagName !== "SCRIPT";
    });
  }

  function contentWrapper(section) {
    var wrap =
      section.querySelector(".section-stack") || section.querySelector(".prose");
    if (!wrap) {
      var kids = realChildren(section);
      wrap = kids[0] || null;
    }
    // Descend through single-child wrappers (e.g. custom sections that nest
    // one container) so we stagger the real blocks, not the outer shell.
    while (wrap) {
      var children = realChildren(wrap);
      if (children.length === 1 && children[0].children.length) {
        wrap = children[0];
      } else {
        break;
      }
    }
    return wrap;
  }

  // The blocks to stagger: real element children of the wrapper, minus any that
  // hold their own self-animating cards (left to the theme).
  function blocksOf(wrap) {
    var out = [];
    var child = wrap.firstElementChild;
    while (child) {
      if (
        child.tagName !== "STYLE" &&
        child.tagName !== "SCRIPT" &&
        !child.matches(SELF_ANIMATING) &&
        !child.querySelector(SELF_ANIMATING)
      ) {
        out.push(child);
      }
      child = child.nextElementSibling;
    }
    return out;
  }

  document.querySelectorAll("main .shopify-section").forEach(function (section) {
    if (CHROME.test(section.className)) return;
    if (section.closest("footer")) return;
    if (section.querySelector(SELF_ANIMATING)) return; // whole section self-animates
    // Above/in the fold at load → leave static (hero handled by CSS). No FOUC.
    if (section.getBoundingClientRect().top < viewportH) return;

    var wrap = contentWrapper(section);
    if (!wrap) return;
    var blocks = blocksOf(wrap);
    if (!blocks.length) blocks = [wrap];

    blocks.forEach(function (block, i) {
      block.style.transitionDelay =
        Math.min(i, MAX_STAGGER_STEPS) * STAGGER + "s";
      block.classList.add("spm-reveal");
    });

    observeReveal(section, function () {
      blocks.forEach(function (block) {
        block.classList.add("is-revealed");
      });
    });
  });

  // Signature — subtle scroll-linked scale on the first hero media (image or video).
  var heroSection = document.querySelector("main .shopify-section");
  var heroMedia =
    heroSection &&
    heroSection.querySelector(".content-over-media img, .content-over-media video");
  if (heroSection && heroMedia) {
    heroMedia.style.willChange = "transform";
    var ticking = false;
    var update = function () {
      var rect = heroSection.getBoundingClientRect();
      var progress = Math.min(1, Math.max(0, -rect.top / (rect.height || 1)));
      heroMedia.style.transform = "scale(" + (1 + 0.06 * progress) + ")";
      ticking = false;
    };
    window.addEventListener(
      "scroll",
      function () {
        if (!ticking) {
          ticking = true;
          window.requestAnimationFrame(update);
        }
      },
      { passive: true }
    );
    update();
  }
})();
